import { useState, useEffect, useRef } from 'react'
import { Box, Button, Stack, Typography } from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import CheckIcon from '@mui/icons-material/Check'
import { useTranslation } from 'react-i18next'
import i18n from '../../../i18n'
import { formatDateTime, formatDate } from '../../../shared/utils'
import { useAppDispatch, useAppSelector } from '../../../store/hooks'
import { setFileMappings, setLatestEtlStatus } from '../../../store/actions'
import FsContainer from '../common/Container'
import FsFileMappings from '../common/FileMappings'
import FsRejectModal from '../common/RejectModal'
import { FsAlertRef } from '../../common/atoms/Alert'
import { useLoading } from '../../../shared/hooks'
import ACTIONS from '../../../store/constants'
import { etlAPI } from '../../../shared/api/ETLAPI'

const FsFilesChecking = ({ setIsLoading }: { setIsLoading: (isLoading: boolean) => void }) => {
  const { t } = useTranslation()
  const [isOpen, setIsOpen] = useState<boolean>(false)
  const { etlFiles, latestEtlStatus, etlFileMappings } = useAppSelector((state) => state.currentEtl)
  const dispatch = useAppDispatch()
  const alertRef = useRef<FsAlertRef>(null)

  const { isFetching } = useLoading([
    ACTIONS.ETL.SET_ETL_FILES,
    ACTIONS.ETL.GET_FILE_MAPPINGS,
    ACTIONS.ETL.SET_ETL_STATUS,
  ])

  useEffect(() => {
    setIsLoading(isFetching)
  }, [isFetching])

  const defaultLocale = i18n.language === 'en' ? 'en' : 'de'
  const upload = latestEtlStatus ? new Date(latestEtlStatus.runAt) : null
  const correspondance = latestEtlStatus ? new Date(latestEtlStatus.dateEffective) : null
  const formattedUploadDate = upload ? formatDateTime(upload, defaultLocale) : null
  const formattedCorrespondanceDate = correspondance
    ? formatDate(correspondance, defaultLocale, 'dd.mm.yyyy')
    : null
  const etlFileNames = etlFiles ? Object.keys(etlFiles) : []

  const canGoNext = () => {
    if (etlFileNames) {
      if (etlFileNames.length === 1) return true
      if (
        etlFileMappings &&
        Object.values(etlFileMappings).every(
          (mapping) => mapping.primaryKey && mapping.referenceFile && mapping.foreignKey,
        )
      ) {
        return true
      }
    }
    return false
  }

  const handleAccept = async () => {
    try {
      setIsLoading(true)
      if (latestEtlStatus) {
        await dispatch(setFileMappings({ mappings: etlFileMappings }))
        await etlAPI.setAcceptOrRejectFiles(latestEtlStatus.id, true)
        dispatch(setLatestEtlStatus())
      }
    } catch (error) {
      alertRef.current?.openAlert({
        severity: 'error',
        message: t('something.went.wrong'),
      })
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <FsContainer
      footer={
        <Stack width="100%" direction="row" justifyContent="flex-end" gap={1}>
          <Button
            onClick={() => setIsOpen(true)}
            startIcon={<CloseIcon />}
            variant="outlined"
            color="error"
          >
            {t('reject')}
          </Button>
          <Button
            startIcon={<CheckIcon />}
            disabled={!canGoNext()}
            onClick={handleAccept}
            variant="contained"
          >
            {t('accept')}
          </Button>
        </Stack>
      }
    >
      <FsRejectModal
        isOpen={isOpen}
        handleClose={() => setIsOpen(false)}
        setIsLoading={setIsLoading}
      />
      <Stack mt={1} ml={0.25}>
        <Stack direction="row" gap={1} alignItems="center">
          <Typography>{t(`uploaded.on`)}</Typography>
          <Box>
            <Typography fontWeight="bold">{formattedUploadDate}</Typography>
          </Box>
        </Stack>
        <Stack direction="row" gap={1} alignItems="center">
          <Typography>{t(`corresponding.to`)}</Typography>
          <Box>
            <Typography fontWeight="bold">{formattedCorrespondanceDate}</Typography>
          </Box>
        </Stack>
        <FsFileMappings setIsLoading={setIsLoading} />
      </Stack>
    </FsContainer>
  )
}

export default FsFilesChecking
